import * as joint from '@joint/plus';
import * as appShapes from '../../../shapes/app-shapes';
import { getAttrsByLinkType } from '../../../shapes/links';
import { PAPER_ZOOM_SETTINGS, STORAGE_KEYS } from '../helpers/constants';

export function initializePaper(service) {
  const graph = (service.graph = new joint.dia.Graph({}, { cellNamespace: appShapes }));
  graph.on('add', (cell, collection, opt) => {
    const existingModule = cell.get('module');
    if (existingModule !== undefined && existingModule !== null && existingModule !== '') {
      return;
    }
    const existingCellName = cell.get('cellName');
    if (existingCellName !== undefined && existingCellName !== null && existingCellName !== '') {
      return;
    }
    const selectedModule = service.highlightedModules?.[0] ?? service.modules?.[0] ?? '';
    cell.prop('module', selectedModule, { silent: true });

    if (cell.isElement()) {
      cell.prop('customAttributes/cellName', '', { silent: true });
    }

    if (cell.get('type') === 'appShapes.Tri') {
      const currentPorts = cell.get('ports/items') ?? [];
      if (!currentPorts.length) {
        const defaultPorts = ['auto'].map((dir, i) => ({
          group: 'auto',
          time: 0,
          destination: null,
          attrs: {
            portLabel: {
              text: String(i + 1),
              fill: service.theme === 'csmdark' ? '#ffffff' : '#2a2a2a',
            },
          },
        }));
        cell.prop('ports/items', defaultPorts, { silent: true });
      }
    }
  });

  service.commandManager = new joint.dia.CommandManager({ graph, cmdBeforeAdd: () => true });
  window.commandManager = service.commandManager;

  const backgroundImage = require('../../../assets/CDG_withBoxes_Full_12kpx_G4.png');

  const interactiveConfig = service.readOnly
    ? {
        vertexAdd: false,
        vertexMove: false,
        vertexRemove: false,
        arrowheadMove: false,
        labelMove: false,
        useLinkTools: false,
        linkMove: false,
        elementMove: false,
        addLinkFromMagnet: false,
        vertexAddable: false,
        stopDelegation: false,
        addVertex: false,
        removeVertex: false,
        allowLink: false,
        markAvailable: false,
      }
    : {
        vertexAdd: true,
        vertexMove: true,
        vertexRemove: true,
        arrowheadMove: true,
        labelMove: true,
        useLinkTools: true,
        linkMove: true,
        elementMove: true,
        addLinkFromMagnet: false,
        vertexAddable: true,
        stopDelegation: false,
        addVertex: true,
        removeVertex: true,
        allowLink: true,
        markAvailable: true,
      };

  const paper = (service.paper = new joint.dia.Paper({
    width: 12000,
    height: 8484,
    gridSize: 2,
    drawGrid: false,
    model: graph,
    cellViewNamespace: appShapes,
    defaultLink: new appShapes.app.Link(),
    defaultConnectionPoint: appShapes.app.Link.connectionPoint,
    background: {
      color: '#FFFFFF',
      image: backgroundImage,
      repeat: 'no-repeat',
      position: { x: 0, y: 0 },
    },
    routerNamespace: {
      normal: joint.routers.normal,
      orthogonal(vertices, opt, linkView) {
        opt.useVertices = true;
        return joint.routers.normal.call(this, vertices, opt, linkView);
      },
    },
    interactive: interactiveConfig,
    defaultRouter: {
      name: 'normal',
      args: {
        padding: 10,
        useVertices: true,
        directions: ['top', 'right', 'bottom', 'left'],
      },
    },
    async: true,
    sorting: joint.dia.Paper.sorting.APPROX,
  }));

  service.snaplines = new joint.ui.Snaplines({ paper });

  if (!service.readOnly) {
    paper.on('blank:contextmenu', ({ clientX, clientY }, x, y) => {
      const bbox = service.graph.getCellsBBox(service.selection.collection.toArray());
      const inside = bbox?.containsPoint({ x, y });
      const cells = inside ? service.selection.collection.toArray() : [];
      service.renderContextToolbar({ x: clientX, y: clientY }, cells);
    });

    paper.on('cell:contextmenu', (cellView, evt) => {
      service.renderContextToolbar({ x: evt.clientX, y: evt.clientY }, [cellView.model]);
    });
  }

  graph.on('change:linkType', (link) => {
    if (link.isLink()) {
      const attrs = getAttrsByLinkType(link.get('linkType'));
      Object.entries(attrs).forEach(([n, v]) => link.attr(n, v));
    }
  });

  const paperScroller = (service.paperScroller = new joint.ui.PaperScroller({
    paper,
    autoResizePaper: false,
    scrollWhileDragging: true,
    padding: { bottom: 100, top: 100, left: 100, right: 550 },
    contentOptions: {
      allowNewOrigin: 'non-negative',
      minWidth: paper.options.width,
      minHeight: paper.options.height,
      maxWidth: paper.options.width * 1.2,
      maxHeight: paper.options.height * 1.2,
    },
    // cursor: 'default',
  }));

  // --- Multi-cell drag context ---
  let multiDragCtx = null;

  (function () {
    function getNativeEvent(evt) {
      return (evt && (evt.originalEvent || evt)) || evt || {};
    }

    paper.on('blank:pointerdown', function (evt) {
      const e = getNativeEvent(evt);
      if (e.button === 1 || (e.button === 0 && e.ctrlKey)) {
        if (e.preventDefault) e.preventDefault();
        paperScroller.setCursor('grab');
        paperScroller.startPanning(e);
        multiDragCtx = null;
      } else {
        paperScroller.setCursor('default');
      }
    });

    paper.on('link:pointerdown', function (linkView, evt) {
      const e = getNativeEvent(evt);
      if (e.button === 1) {
        if (e.preventDefault) e.preventDefault();
        if (e.stopPropagation) e.stopPropagation();
        paperScroller.startPanning(e);
        multiDragCtx = null;
      } else if (e.button === 0 && e.ctrlKey) {
        if (e.stopPropagation) e.stopPropagation();
        paperScroller.startPanning(e);
        multiDragCtx = null;
      }
    });

    document.addEventListener('mouseup', function () {
      if (paperScroller.isPanning && paperScroller.isPanning()) {
        paperScroller.stopPanning();
      }
    });
  })();

  // cell:pointerdown – combine panning and multi-cell drag
  paper.on('cell:pointerdown', function (cellView, evt, x, y) {
    function getNativeEvent(e) {
      return (e && (e.originalEvent || e)) || e || {};
    }
    const e = getNativeEvent(evt);

    // Middle button or Ctrl+left still starts panning
    if (e.button === 1) {
      if (e.preventDefault) e.preventDefault();
      if (e.stopPropagation) e.stopPropagation();
      paperScroller.startPanning(e);
      multiDragCtx = null;
      return;
    } else if (e.button === 0 && e.ctrlKey) {
      if (e.stopPropagation) e.stopPropagation();
      paperScroller.startPanning(e);
      multiDragCtx = null;
      return;
    }

    // Multi-cell drag (left button, no Ctrl, not read-only)
    if (!service.readOnly && e.button === 0 && !e.ctrlKey) {
      const model = cellView.model;
      const selection = service.selection;
      const collection = selection && selection.collection;

      if (
        collection &&
        collection.length > 1 &&
        collection.get(model) && // clicked cell is in the selection
        model.isElement()
      ) {
        const selectedElements = collection.toArray().filter((c) => c.isElement());

        // Collect unique links connected to any selected element
        const linksMap = new Map();
        selectedElements.forEach((el) => {
          const connected = graph.getConnectedLinks(el) || [];
          connected.forEach((link) => {
            linksMap.set(link.id, link);
          });
        });
        const connectedLinks = Array.from(linksMap.values());

        multiDragCtx = {
          lastPointer: { x, y },
          elements: selectedElements,
          links: connectedLinks,
        };

        if (e.preventDefault) e.preventDefault();
      } else {
        multiDragCtx = null;
      }
    } else {
      multiDragCtx = null;
    }
  });

  // Move all selected elements + their connected links while dragging
  paper.on('cell:pointermove', function (cellView, evt, x, y) {
    if (!multiDragCtx) return;
    if (service.readOnly) return;

    const dx = x - multiDragCtx.lastPointer.x;
    const dy = y - multiDragCtx.lastPointer.y;

    if (!dx && !dy) return;

    // Move elements
    multiDragCtx.elements.forEach((cell) => {
      cell.translate(dx, dy);
    });

    // Move connected links (vertices, labels, etc.)
    multiDragCtx.links.forEach((link) => {
      link.translate(dx, dy);
    });

    multiDragCtx.lastPointer = { x, y };
  });

  // Clear drag context on mouse up
  paper.on('cell:pointerup', function () {
    multiDragCtx = null;
  });

  let saveTimeout;
  const saveMapStateThrottled = (zoom, position) => {
    clearTimeout(saveTimeout);
    saveTimeout = setTimeout(() => {
      localStorage.setItem(STORAGE_KEYS.MAP_ZOOM_LEVEL, zoom.toString());
      localStorage.setItem(STORAGE_KEYS.MAP_POSITION, JSON.stringify(position));
    }, 200);
  };

  const restoreMapState = () => {
    const savedPosition = localStorage.getItem(STORAGE_KEYS.MAP_POSITION);
    const savedZoom = localStorage.getItem(STORAGE_KEYS.MAP_ZOOM_LEVEL);
    const currentUrl = window.location.href;
    const lastScenarioUrl = localStorage.getItem(STORAGE_KEYS.LAST_SCENARIO);
    const isNewScenario = currentUrl !== lastScenarioUrl;

    localStorage.setItem(STORAGE_KEYS.LAST_SCENARIO, currentUrl);

    if (isNewScenario) {
      paperScroller.zoom(1, { absolute: true });
      localStorage.setItem(STORAGE_KEYS.MAP_ZOOM_LEVEL, '1');
    } else {
      if (savedZoom) {
        const zoomLevel = parseFloat(savedZoom);
        if (zoomLevel >= PAPER_ZOOM_SETTINGS.min && zoomLevel <= PAPER_ZOOM_SETTINGS.max) {
          paperScroller.zoom(zoomLevel, { absolute: true });
        }
      }

      if (savedPosition) {
        try {
          const position = JSON.parse(savedPosition);
          setTimeout(() => {
            paperScroller.center(position.x, position.y);
          }, 0);
        } catch (e) {
          console.error('Error parsing saved position:', e);
        }
      }
    }
  };

  const updateMapStateAndNavigator = (zoom, position) => {
    saveMapStateThrottled(zoom, position);
    if (service.navigatorService?.navigator) {
      service.navigatorService.navigator.updateCurrentView();
    }
  };

  paperScroller.on('scale', (sx) => {
    const position = paperScroller.getCenter();
    updateMapStateAndNavigator(sx, position);
  });

  paper.on('scale', (sx) => {
    const position = paperScroller.getCenter();
    updateMapStateAndNavigator(sx, position);
  });

  paperScroller.on('scroll', () => {
    const position = paperScroller.getCenter();
    const zoom = paperScroller.zoom();
    updateMapStateAndNavigator(zoom, position);
  });

  graph.on({
    'change:paperColor': (_, color) => paper.drawBackground({ color }),
    'change:background': (_, bg) =>
      paper.drawBackground({
        image: bg ? backgroundImage : null,
        repeat: 'no-repeat',
        position: { x: 0, y: 0 },
      }),
    'change:infinitePaper': (_, borderless) => {
      const opts = paperScroller.options;
      if (borderless) {
        opts.borderless = true;
        opts.baseWidth = 100;
        opts.baseHeight = 100;
        service.paperContainer.classList.remove('bordered');
      } else {
        opts.borderless = false;
        opts.baseWidth = service.defaultPaperSize.width;
        opts.baseHeight = service.defaultPaperSize.height;
        service.paperContainer.classList.add('bordered');
      }
      paperScroller.adjustPaper();
    },
    'change:dotGrid': (_, show) => paper.setGrid(show),
    'change:snaplines': (_, allow) => service.changeSnapLines(allow),
    'change:gridSize': (_, size) => paper.setGridSize(size),
  });

  if (service.paperContainer) {
    service.paperContainer.innerHTML = '';
    service.paperContainer.appendChild(paperScroller.el);
  }
  paperScroller.center(600, 1750);

  paper.on('paper:pinch', (_evt, ox, oy, scale) => {
    const zoom = paperScroller.zoom();
    const newZoom = zoom * scale;
    paperScroller.zoom(newZoom, {
      min: PAPER_ZOOM_SETTINGS.min,
      max: PAPER_ZOOM_SETTINGS.max,
      ox,
      oy,
      absolute: true,
      callback: () => paperScroller.adjustScrollbars(),
    });
  });

  setTimeout(() => {
    restoreMapState();
  }, 0);
}
